import axios from 'axios';

// Base API configuration
const API_BASE_URL = '/';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `JWT ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// API Services
export const apiService = {
  // Authentication
  auth: {
    login: (credentials) => apiClient.post('/auth/login/', credentials),
    register: (userData) => apiClient.post('/auth/register/', userData),
    logout: () => apiClient.post('/auth/logout/'),
  },

  // Submissions
  submissions: {
    list: (params) => apiClient.get('/submissions/', { params }),
    get: (submissionId) => apiClient.get(`/submission/${submissionId}/results`),
    compare: (submissionId) => apiClient.get(`/submission/${submissionId}/compare`),
    delete: (submissionId) => apiClient.delete(`/submissions/${submissionId}/`),
  },

  // Candidates
  candidates: {
    evaluate: (data) => apiClient.post('/candidate/evaluate', data),
    autoEvaluate: (data) => apiClient.post('/auto-evaluate/', data),
    agenticEvaluate: (data) => apiClient.post('/agentic-evaluate/candidate/', data),
    generateReport: (data) => apiClient.post('/candidate/report', data, {
      responseType: 'blob'
    }),
    getEvaluations: (candidateId) => apiClient.get(`/candidates/${candidateId}/evaluations/`),
    reevaluate: (candidateId, data = {}) => apiClient.post(`/candidates/${candidateId}/reevaluate/`, data),
  },

  // Comparisons
  comparisons: {
    compareCandidate: (candidateId) => apiClient.get('/compare-scores/', {
      params: { candidate_id: candidateId }
    }),
    compareMultiple: (candidateIds, mode = 'first_run', evaluationType = 'all') => apiClient.post('/compare-scores/multiple/', {
      candidate_ids: candidateIds,
      mode,
      evaluation_type: evaluationType,
    }, { timeout: 120000 }),
    getStats: () => apiClient.get('/compare-scores/stats/'),
    bulkUpdateScores: (data) => apiClient.post('/bulk-update-scores/', data),
    bulkEvaluate: (data) => apiClient.post('/bulk-evaluate/', data),
    reevaluateAll: (data) => apiClient.post('/reevaluate-all/', data),
    rescoreAspect: (data) => apiClient.post('/rescore-aspect/', data),
    getRescoreDiff: (controlSumId, evaluationType = 'all') =>
      apiClient.get('/rescore-diff/', {
        params: { control_sum_id: controlSumId, evaluation_type: evaluationType },
        timeout: 120000,
      }),
  },

  // Aspects
  aspects: {
    get: (toolId) => apiClient.post('/aspects/get', { tool_id: toolId }),
    list: (toolId) => apiClient.get('/aspects/', { params: { tool_id: toolId } }),
  },

  // Agentic evaluation
  agentic: {
    evaluateAspect: (data) => apiClient.post('/agentic-evaluate/', data),
  },

  // Guidelines
  guidelines: {
    preview: (formData) => apiClient.post('/guidelines/preview/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
    save: (data) => apiClient.post('/guidelines/save/', data),
    list: (toolId) => apiClient.get('/guidelines/', { params: { tool_id: toolId } }),
    get: (id) => apiClient.get(`/guidelines/${id}/`),
    create: (data) => apiClient.post('/guidelines/', data),
    update: (id, data) => apiClient.patch(`/guidelines/${id}/`, data),
    delete: (id) => apiClient.delete(`/guidelines/${id}/`),
  },

  // Few-Shot Examples
  fewShotExamples: {
    list: (toolId) => apiClient.get('/few-shot-examples/', { params: { tool_id: toolId } }),
    get: (id) => apiClient.get(`/few-shot-examples/${id}/`),
    create: (data) => apiClient.post('/few-shot-examples/', data),
    update: (id, data) => apiClient.patch(`/few-shot-examples/${id}/`, data),
    delete: (id) => apiClient.delete(`/few-shot-examples/${id}/`),
  },

  // Ground Truth Explanation
  groundTruth: {
    explain: (data) => apiClient.post('/ground-truth-explain/', data, {
      timeout: 300000,
    }),
  },

  // Few-Shot Validation
  fewShotValidation: {
    start: (data) => apiClient.post('/few-shot-validation/start/', data),
    getStatus: (validationId) => apiClient.get(`/few-shot-validation/${validationId}/`),
    list: (params) => apiClient.get('/few-shot-validation/', { params }),
  },

  // Score Pattern Discovery
  scorePatternDiscovery: {
    start: (data) => apiClient.post('/score-pattern-discovery/', data, { timeout: 600000 }),
    getStatus: (discoveryId) => apiClient.get(`/score-pattern-discovery/${discoveryId}/`),
  },

  // Few-Shot Builder
  fewShotBuilder: {
    list: () => apiClient.get('/few-shot-builder/'),
    start: (data) => apiClient.post('/few-shot-builder/start/', data, { timeout: 600000 }),
    getStatus: (bulkGroupId) => apiClient.get(`/few-shot-builder/${bulkGroupId}/`),
  },
};

export default apiService;
